import { ClickAwayListener } from "@mui/base";
import type { FunctionComponent } from "react";
import { useEffect, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";

import type { DefaultComponentsProvider } from "~/components/article/article-render";
import { useTimelineScrollEffect } from "~/components/article/article-render/live-article/hooks";
import { ContentSchemaRender } from "~/components/content/content-schema-render";
import { useIntersection } from "~/lib/hooks";
import { formatInTimeZone } from "~/lib/utils";
import type { itemLiveArticleContentArticle$key } from "~/queries/__generated__/itemLiveArticleContentArticle.graphql";
import type { itemLiveArticleContentLiveContent$key } from "~/queries/__generated__/itemLiveArticleContentLiveContent.graphql";

import {
  ActionBox,
  Authors,
  Container,
  Content,
  Header,
  LinePrefix,
  LineSuffix,
  ShareBar,
  ShareButton,
  ShareLabel,
  StyledContentShareWidget,
  StyledShareButtonIcon,
  StyledTooltip,
  TheEnd,
  Time,
  Title,
} from "./styles";

type Props = {
  className?: string;
  id?: string;
  isTheEnd?: boolean;
  liveArticleReference: itemLiveArticleContentArticle$key;
  liveContentReference: itemLiveArticleContentLiveContent$key;
  provider: DefaultComponentsProvider;
};

export const LiveArticleContentItem: FunctionComponent<Props> = ({
  className,
  id,
  isTheEnd = false,
  liveArticleReference,
  liveContentReference,
  provider,
}) => {
  const containerReference = useRef<HTMLDivElement>(null);

  const [isShowShareBar, setIsShowShareBar] = useState(false);
  const { captureIntersectionElement, intersection } = useIntersection({
    threshold: [0.2],
  });

  const { setToScroll } = useTimelineScrollEffect();

  const article = useFragment(
    graphql`
      fragment itemLiveArticleContentArticle on Article {
        ...contentSchemaRenderContent
        ...contentShareWidgetContent
      }
    `,
    liveArticleReference,
  );

  const content = useFragment(
    graphql`
      fragment itemLiveArticleContentLiveContent on LiveContent {
        entityId
        title
        authors {
          name
        }
        body {
          json
        }
        customTime
      }
    `,
    liveContentReference,
  );

  const renderAuthors = () => {
    const prefix = "From ";
    const authors = content?.authors ?? [];
    if (authors.length === 0) return null;
    return (
      prefix +
      authors
        .map(author => author?.name ?? "")
        .join(", ")
        .replace(/(, )([^,]+)$/, ` and $2`)
    );
  };

  useEffect(() => {
    if (!intersection || !intersection.isIntersecting || !content?.entityId) return;
    setToScroll(content.entityId, "timeline");
  }, [intersection, content.entityId, setToScroll]);

  return (
    <Container className={className} id={id} ref={containerReference}>
      <Header>
        <LinePrefix />
        {content?.customTime && <Time>{formatInTimeZone(content.customTime, "h:mma")}</Time>}
        {isTheEnd && <TheEnd>The End</TheEnd>}
        <LineSuffix />
      </Header>
      <Title>{content.title}</Title>
      <Authors>{renderAuthors()}</Authors>
      <Content>
        <ContentSchemaRender reference={article} schema={content.body?.json} />
      </Content>
      <ActionBox>
        {provider.commentTrigger({ variant: "icon" })}
        <StyledTooltip
          open={isShowShareBar}
          placement="bottom-start"
          PopperProps={{
            disablePortal: true,
          }}
          title={
            <ClickAwayListener
              onClickAway={() => {
                setIsShowShareBar(false);
              }}
            >
              <ShareBar>
                <ShareLabel>Share</ShareLabel>
                <StyledContentShareWidget
                  extraProps={{
                    copyLink: {
                      tooltipPlacement: "top",
                      variant: "tooltip",
                    },
                  }}
                  reference={article}
                  style={{
                    backgroundColor: "transparent",
                    color: "#001246",
                  }}
                  types={["facebook", "twitter", "copy-link", "email"]}
                />
              </ShareBar>
            </ClickAwayListener>
          }
        >
          <ShareButton
            onClick={() => {
              setIsShowShareBar(!isShowShareBar);
            }}
          >
            <StyledShareButtonIcon />
          </ShareButton>
        </StyledTooltip>
      </ActionBox>
      <div ref={captureIntersectionElement} />
    </Container>
  );
};

LiveArticleContentItem.displayName = "LiveArticleContentItem";
